'use client';

import { useRunnerRequestUiRuntime } from '../runtime';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { type JSONContent } from '@tiptap/core';
import { ArrowUpRight, ChevronDown, HelpCircle } from 'lucide-react';
import { daemonTriggerStatusBadge } from '../lib/badgeColors';
import { type ActiveConcurrencyWarningReason } from '../lib/daemonTriggerConflict';
import { activeConcurrencyWarningMessageKey } from '../lib/daemonTriggerConflictMessages';
import { Select, type SelectOption } from './Select';
import { EffortSlider } from './EffortSlider';
import { TiptapEditor } from './TiptapEditor';
import { BaseModal } from './BaseModal';
import { useIsMdUp } from '../hooks/useIsMdUp';
import { useDispatchPolicySelectionSync } from '../hooks/useDispatchPolicySelectionSync';
import { PromptPresetModal } from './PromptPresetModal';
import { UserConventionSelector, type UserConventionOption } from './UserConventionSelector';
import { appendPromptContent, hasEntityReference, removeEntityReference } from './daemonTriggerDetailUtils';
import { AttachmentPendingChips } from './AttachmentPendingChips';
import { AttachmentAddButton } from './AttachmentAddButton';
import { AttachmentDropZone } from './AttachmentDropZone';
import { DaemonTriggerWorktreeSettings } from './DaemonTriggerWorktreeSettings';
import { DaemonTriggerDispatchPolicyPicker } from './DaemonTriggerDispatchPolicyPicker';
import { cn } from '../lib/cn';
import { noRunnerDispatchPolicyValue } from '../lib/runnerDispatchPolicySelection';
import { buildEntityReferencePrefillPrompt } from '../lib/entityReferencePrompt';
import type { PendingAttachmentDraft } from '../types/attachment';
import type {
  DaemonTriggerResolvedSettings,
  RunnerDispatchPolicy,
  RunnerDispatchPolicyEntityType,
  RunnerDispatchPolicyRecommendation,
} from '../lib/api';
type DaemonTriggerModalMode = 'create' | 'continue';

type DaemonTriggerModalState = {
  selectedProjectId: string;
  canChangeProject: boolean;
  isProjectSelectionLoading: boolean;
  daemonPlanMode: boolean;
  worktree: {
    daemonUseWorktree: boolean;
    worktreeSelectionMode: 'new' | 'existing';
    selectedExistingWorktreeId: string;
    selectedExistingRepositoryId: string;
    daemonRepositoryId: string;
    daemonBaseBranch: string;
    branchInputMode: 'dropdown' | 'manual';
    branchInputHelpMessage: string | null;
    showProjectRepositorySettingsCta: boolean;
    worktreeWarningMessage: string | null;
    isWorktreeUsageDisabled: boolean;
    isRepositoryBranchesFetching: boolean;
    isActiveWorktreesFetching: boolean;
    isActiveWorktreesError: boolean;
    isExistingModeDisabled: boolean;
  };
  isActiveRequestsSummaryExpanded: boolean;
  daemonId: string;
  daemonAgentConfigId: string;
  daemonRunnerType: string;
  daemonModel: string;
  daemonEffort: string;
  isSelectedDaemonPlanModeOnly: boolean;
  isPlanModeForced: boolean;
  daemonPrompt: JSONContent | undefined;
  promptTextLength: number;
  promptMaxLength: number;
  isPromptOverLimit: boolean;
  activeConcurrencyWarningReason: ActiveConcurrencyWarningReason | null;
  daemonConflictSelectButtonClassName?: string;
  isBoundDaemonOffline: boolean;
  resolvedSettings: DaemonTriggerResolvedSettings | null;
  isResolvedSettingsError: boolean;
  hasConnectedDaemon: boolean;
  selectableDaemonCount: number;
  isInitialLoading: boolean;
  isSubmitPending: boolean;
  isSubmitDisabled: boolean;
  selectedUserConventionIds: string[];
  attachmentDrafts: PendingAttachmentDraft[];
  dispatchPolicy: {
    dispatchContext?: {
      entityType: RunnerDispatchPolicyEntityType;
      entityId: string;
      entityState: string;
      entityLabel?: string;
    };
    selectedDispatchPolicyId: string | null;
    dispatchPolicies: RunnerDispatchPolicy[];
    dispatchPolicyRecommendation?: RunnerDispatchPolicyRecommendation;
  };
};

type DaemonTriggerModalOptions = {
  projectOptions: SelectOption[];
  activeDaemonTriggers: Array<{
    id: string;
    status: string;
    agentConfigId: string;
    planMode: boolean;
    useWorktree: boolean;
    runnerType: string;
  }>;
  agentConfigNameById: Record<string, string>;
  daemonOptions: SelectOption[];
  daemonAgentConfigOptions: SelectOption[];
  daemonRunnerTypeOptions: SelectOption[];
  daemonModelOptions: SelectOption[];
  daemonEffortOptions: SelectOption[];
  // 선택된 모델이 Effort 레벨을 지원할 때만 true. false면 Effort Select를 렌더하지 않는다.
  showEffortSelect: boolean;
  worktree: {
    daemonRepositoryOptions: SelectOption[];
    daemonBranchOptions: SelectOption[];
    existingWorktreeOptions: SelectOption[];
    existingRepositoryOptions: SelectOption[];
  };
  userConventionOptions: UserConventionOption[];
};

type DaemonTriggerModalActions = {
  onClose: () => void;
  onProjectChange: (value: string) => void;
  worktree: {
    onDismissWorktreeWarning: () => void;
    onDaemonUseWorktreeChange: (checked: boolean) => void;
    onWorktreeSelectionModeChange: (value: 'new' | 'existing') => void;
    onExistingWorktreeChange: (value: string) => void;
    onExistingRepositoryChange: (value: string) => void;
    onDaemonRepositoryIdChange: (value: string) => void;
    onDaemonBaseBranchChange: (value: string) => void;
    onOpenProjectRepositorySettings: () => void;
  };
  onToggleActiveRequestsSummary: () => void;
  onDaemonIdChange: (value: string) => void;
  onDaemonAgentConfigIdChange: (value: string) => void;
  onDaemonRunnerTypeChange: (value: string) => void;
  onDaemonModelChange: (value: string) => void;
  onDaemonEffortChange: (value: string) => void;
  onDaemonPlanModeChange: (checked: boolean) => void;
  onDaemonPromptChange: (value: JSONContent | undefined) => void;
  onToggleUserConventionId: (conventionId: string, checked: boolean) => void;
  onUserConventionIdsChange: (value: string[]) => void;
  onAttachmentDraftsChange: (value: PendingAttachmentDraft[]) => void;
  dispatchPolicy: {
    onDispatchPolicyChange: (value: string | null) => void;
    onRestoreRecommendedPolicy?: () => void;
  };
  onViewSourceTrigger?: () => void;
  onSubmit: () => void;
};

type DaemonTriggerModalPreset = {
  token: string | null;
  projectId: string;
  memberId: string | null;
  /** 프리셋 쓰기 가능 여부. 미지정 시 기존 동작(쓰기 UI 표시)을 유지한다. */
  canWrite?: boolean;
};

type DaemonTriggerModalProps = {
  isOpen: boolean;
  mode: DaemonTriggerModalMode;
  /**
   * true일 때 모달을 화면에서 숨기지만 state는 모두 보존한다(display:none).
   * 원본 상세 모달이 위에 떴을 때 같은 레이어 stacking 충돌을 피하기 위함.
   */
  isMuted?: boolean;
  requestsHref: string;
  state: DaemonTriggerModalState;
  options: DaemonTriggerModalOptions;
  actions: DaemonTriggerModalActions;
  preset?: DaemonTriggerModalPreset;
};

export const DaemonTriggerModal = ({
  isOpen,
  mode,
  isMuted = false,
  requestsHref,
  state,
  options,
  actions,
  preset,
}: DaemonTriggerModalProps) => {
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);
  const { useIsDesktopApp } = useRunnerRequestUiRuntime();
  const isDesktopApp = useIsDesktopApp();
  // 데스크탑 앱에서는 webview 폭과 무관하게 항상 데스크탑(펼친) 폼 레이아웃을 쓴다.
  const desktopLayout = useIsMdUp() || isDesktopApp;

  // RunnerBox를 켜는 순간에는 리포지토리/브랜치를 골라야 하므로 항상 펼친 상태로 시작한다.
  const [isRunnerBoxSettingsOpen, setIsRunnerBoxSettingsOpen] = useState(true);
  useEffect(() => {
    if (state.worktree.daemonUseWorktree) {
      setIsRunnerBoxSettingsOpen(true);
    }
  }, [state.worktree.daemonUseWorktree]);

  // 프롬프트에 처음 포커스가 들어오면 상단 설정(고급/RunnerBox)을 한 번 접어
  // 입력 공간을 넓힌다. 1회만 동작하므로 이후 사용자가 다시 펼친 상태나
  // RunnerBox 자동펼침과 충돌하지 않는다. 모달이 새로 열릴 때마다 다시 허용한다.
  const hasAutoCollapsedSettingsRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      hasAutoCollapsedSettingsRef.current = false;
    }
  }, [isOpen]);
  const handlePromptFocus = () => {
    if (hasAutoCollapsedSettingsRef.current) {
      return;
    }
    hasAutoCollapsedSettingsRef.current = true;
    setIsAdvancedSettingsOpen(false);
    setIsRunnerBoxSettingsOpen(false);
  };

  const tLayout = useTranslations('components.appLayout');
  const tApiErrorDetails = useTranslations('apiErrorDetails');
  const tCommon = useTranslations('common');
  const tNav = useTranslations('nav');
  const resolveTriggerStatusBadge = (status: string) => {
    return (
      daemonTriggerStatusBadge[status as keyof typeof daemonTriggerStatusBadge] ?? 'bg-base-300 text-base-content/70'
    );
  };

  const SelectSkeleton = () => (
    <div className="h-8 w-full animate-pulse rounded-lg bg-base-300/60" aria-hidden="true" />
  );
  const shouldShowSelectSkeleton = (optionsLength: number) => state.isInitialLoading && optionsLength <= 1;

  // 접힌 섹션 헤더에 현재 선택값을 한 줄 요약으로 노출하기 위한 라벨 매핑.
  const labelOf = (opts: SelectOption[], value: string) => opts.find((option) => option.value === value)?.label ?? '';
  const advancedSettingsSummary = [
    labelOf(options.daemonOptions, state.daemonId),
    labelOf(options.daemonAgentConfigOptions, state.daemonAgentConfigId),
    labelOf(options.daemonRunnerTypeOptions, state.daemonRunnerType),
    labelOf(options.daemonModelOptions, state.daemonModel),
    options.showEffortSelect ? labelOf(options.daemonEffortOptions, state.daemonEffort) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const resolvedSettingsErrors = state.resolvedSettings?.errors ?? [];
  const shouldShowResolvedSettingsNotice =
    mode === 'create' && (state.isResolvedSettingsError || resolvedSettingsErrors.length > 0);
  const selectedDispatchPolicy =
    state.dispatchPolicy.dispatchPolicies.find(
      (policy) => policy.id === state.dispatchPolicy.selectedDispatchPolicyId,
    ) ?? null;
  // 정책 프리셋의 기본값(플랜 모드 + 주입 컨벤션)은 선택 직후 모달 상태에 반영하되, 이후
  // 사용자의 변경은 같은 정책 선택을 유지하는 한 다시 덮어쓰지 않는다. (see useDispatchPolicySelectionSync)
  useDispatchPolicySelectionSync(
    isOpen,
    selectedDispatchPolicy,
    actions.onDaemonPlanModeChange,
    actions.onUserConventionIdsChange,
  );

  useEffect(() => {
    if (!state.dispatchPolicy.dispatchContext) return;
    const entityTypeByDispatchType: Record<RunnerDispatchPolicyEntityType, string> = {
      PLAN: 'plan',
      CO_ACTION: 'coAction',
      COMPLETION_REPORT: 'completionReport',
      POST_MORTEM: 'postMortem',
      CODE_REVIEW: 'codeReview',
    };
    const nextPrompt = selectedDispatchPolicy
      ? removeEntityReference(state.daemonPrompt, {
          entityType: entityTypeByDispatchType[state.dispatchPolicy.dispatchContext.entityType],
          entityId: state.dispatchPolicy.dispatchContext.entityId,
        })
      : state.dispatchPolicy.selectedDispatchPolicyId === noRunnerDispatchPolicyValue &&
          !hasEntityReference(state.daemonPrompt, {
            entityType: entityTypeByDispatchType[state.dispatchPolicy.dispatchContext.entityType],
            entityId: state.dispatchPolicy.dispatchContext.entityId,
          })
        ? appendPromptContent(
            state.daemonPrompt,
            buildEntityReferencePrefillPrompt({
              entityType: state.dispatchPolicy.dispatchContext.entityType as
                | 'PLAN'
                | 'CO_ACTION'
                | 'COMPLETION_REPORT'
                | 'POST_MORTEM'
                | 'CODE_REVIEW',
              entityId: state.dispatchPolicy.dispatchContext.entityId,
              label:
                state.dispatchPolicy.dispatchContext.entityLabel ??
                `${state.dispatchPolicy.dispatchContext.entityType} ${state.dispatchPolicy.dispatchContext.entityId.slice(0, 8)}`,
            }),
          )
        : state.daemonPrompt;
    if (nextPrompt === state.daemonPrompt) return;
    const frame = window.requestAnimationFrame(() => actions.onDaemonPromptChange(nextPrompt));
    return () => window.cancelAnimationFrame(frame);
  }, [
    actions,
    selectedDispatchPolicy,
    state.daemonPrompt,
    state.dispatchPolicy.dispatchContext,
    state.dispatchPolicy.selectedDispatchPolicyId,
  ]);

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={actions.onClose}
        canClose={!state.isSubmitPending}
        isHidden={isMuted}
        variant="bottomSheet"
        size="xl"
        backdropClassName="[backface-visibility:hidden]"
        className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden overscroll-contain sm:max-h-[calc(100vh-3rem)]"
      >
        <div className="mb-4 flex shrink-0 items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            {mode === 'create' && state.daemonPlanMode ? (
              <span className="shrink-0 rounded-full bg-base-content/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-base-content/70">
                Plan
              </span>
            ) : null}
            {mode === 'create' && state.worktree.daemonUseWorktree ? (
              <span className="shrink-0 rounded-full bg-info/15 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-info">
                RunnerBox
              </span>
            ) : null}
            <h3 className="truncate text-base font-semibold">
              {mode === 'continue' ? tLayout('daemonTriggerContinueRequest') : tLayout('daemonTriggerRequest')}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <Link
              href={requestsHref}
              className="btn btn-ghost btn-circle btn-sm"
              title={tNav('daemonTriggers')}
              onClick={actions.onClose}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.818a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.845-.143z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
            <button type="button" className="btn btn-ghost btn-circle btn-sm" onClick={actions.onClose}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3 transition-opacity ${state.isSubmitPending ? 'pointer-events-none opacity-60' : ''}`}
          aria-disabled={state.isSubmitPending}
        >
          {/* 부트스트랩/리소스 초기 로딩 중 상단 얇은 진행 표시. 프롬프트 입력은 그대로 가능하고 제출만 게이트된다.
              러너가 아직 안 잡힌 초기 로드에만 표시해 백그라운드 폴링(activeTriggers) 재요청 시 깜빡이지 않게 한다. */}
          {mode === 'create' && state.isInitialLoading && state.daemonId.length === 0 ? (
            <progress className="progress progress-primary h-0.5 w-full shrink-0" aria-hidden />
          ) : null}
          {/* 본문 스크롤 영역: 항목이 많아도 아래 고정 푸터(첨부 칩·충돌 배너·버튼)와 겹치지 않게 한다. */}
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain">
            <div className="shrink-0 space-y-3">
              {mode === 'continue' ? (
                <>
                  <div className="rounded-lg border border-base-300 bg-base-200 p-3 text-xs text-base-content/70">
                    {tLayout('daemonTriggerContinueDescription')}
                  </div>
                  {actions.onViewSourceTrigger ? (
                    <button
                      type="button"
                      onClick={actions.onViewSourceTrigger}
                      className="inline-flex items-center gap-1 self-start text-xs text-primary hover:underline"
                    >
                      {tLayout('viewSourceTrigger')}
                      <ArrowUpRight className="h-3 w-3" aria-hidden />
                    </button>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex min-w-0 flex-col">
                      <label
                        className="mb-1.5 text-xs font-medium text-base-content/50"
                        htmlFor="daemon-continue-runner-type-select"
                      >
                        {tLayout('runnerType')}
                      </label>
                      {shouldShowSelectSkeleton(options.daemonRunnerTypeOptions.length) ? (
                        <SelectSkeleton />
                      ) : (
                        <Select
                          id="daemon-continue-runner-type-select"
                          size="sm"
                          value={state.daemonRunnerType}
                          onChange={actions.onDaemonRunnerTypeChange}
                          options={options.daemonRunnerTypeOptions}
                          fullWidth
                        />
                      )}
                    </div>
                    <div className="relative flex min-w-0 flex-col">
                      <label
                        className="mb-1.5 flex h-4 items-center text-xs font-medium text-base-content/50"
                        htmlFor="daemon-continue-model-select"
                      >
                        {tLayout('runnerModel')}
                      </label>
                      {options.showEffortSelect ? (
                        <EffortSlider
                          className="absolute right-0 top-0 -translate-y-0.5"
                          id="daemon-continue-effort-select"
                          value={state.daemonEffort}
                          onChange={actions.onDaemonEffortChange}
                          options={options.daemonEffortOptions}
                          title={tLayout('runnerEffort')}
                          autoLabel={tLayout('daemonEffortAuto')}
                          compactAutoLabel={tLayout('daemonEffortDefaultShort')}
                          fasterLabel={tLayout('daemonEffortFaster')}
                          smarterLabel={tLayout('daemonEffortSmarter')}
                        />
                      ) : null}
                      {shouldShowSelectSkeleton(options.daemonModelOptions.length) ? (
                        <SelectSkeleton />
                      ) : (
                        <Select
                          id="daemon-continue-model-select"
                          size="sm"
                          value={state.daemonModel}
                          onChange={actions.onDaemonModelChange}
                          disabled={state.daemonRunnerType.length === 0}
                          options={options.daemonModelOptions}
                          fullWidth
                        />
                      )}
                    </div>
                  </div>
                  {options.userConventionOptions.length > 0 ? (
                    <UserConventionSelector
                      label={tLayout('userConventionsLabel')}
                      emptyHint={tLayout('userConventionsEmpty')}
                      conventions={options.userConventionOptions}
                      selectedIds={state.selectedUserConventionIds}
                      onToggle={actions.onToggleUserConventionId}
                    />
                  ) : null}
                </>
              ) : (
                <div className="space-y-2">
                  {state.canChangeProject ? (
                    <div className="mb-3 space-y-2">
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs font-medium text-base-content/50" htmlFor="runner-project-select">
                          {tLayout('runnerProject')}
                        </label>
                        <button
                          type="button"
                          className={`${desktopLayout ? 'hidden' : 'md:hidden'} inline-flex items-center text-base-content/40 hover:text-base-content/60`}
                          title={
                            state.isProjectSelectionLoading
                              ? tLayout('runnerProjectLoading')
                              : tLayout('runnerProjectChangeHint')
                          }
                          aria-label={tLayout('runnerProjectChangeHint')}
                        >
                          <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                      <Select
                        id="runner-project-select"
                        size="sm"
                        value={state.selectedProjectId}
                        onChange={actions.onProjectChange}
                        options={options.projectOptions}
                        disabled={state.isProjectSelectionLoading || options.projectOptions.length <= 1}
                        fullWidth
                      />
                      <p className={`${desktopLayout ? 'block' : 'hidden md:block'} text-[11px] text-base-content/45`}>
                        {state.isProjectSelectionLoading
                          ? tLayout('runnerProjectLoading')
                          : tLayout('runnerProjectChangeHint')}
                      </p>
                    </div>
                  ) : null}
                  {options.activeDaemonTriggers.length > 0 ? (
                    <div className="rounded-lg border border-info/20 bg-info/5 px-3 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-medium text-base-content/70">
                          {tLayout('activeRequestsSummaryTitle', { count: options.activeDaemonTriggers.length })}
                        </p>
                        <button
                          type="button"
                          className="text-[11px] font-medium text-info transition hover:text-info/80"
                          onClick={actions.onToggleActiveRequestsSummary}
                        >
                          {state.isActiveRequestsSummaryExpanded ? tLayout('hide') : tLayout('show')}
                        </button>
                      </div>
                      {state.isActiveRequestsSummaryExpanded ? (
                        <ul className="mt-2 space-y-1">
                          {options.activeDaemonTriggers.slice(0, 3).map((trigger) => (
                            <li key={trigger.id} className="flex items-center gap-2 text-[11px] text-base-content/60">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${resolveTriggerStatusBadge(trigger.status)}`}
                              >
                                {trigger.status}
                              </span>
                              <span className="truncate">
                                {options.agentConfigNameById[trigger.agentConfigId] ??
                                  trigger.agentConfigId.slice(0, 8)}
                              </span>
                              {trigger.planMode ? (
                                <span className="shrink-0 rounded-full bg-base-content/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-base-content/70">
                                  Plan
                                </span>
                              ) : null}
                              {trigger.useWorktree ? (
                                <span className="shrink-0 rounded-full bg-info/15 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-info">
                                  RunnerBox
                                </span>
                              ) : null}
                              <span className="text-base-content/40">{trigger.runnerType}</span>
                            </li>
                          ))}
                          {options.activeDaemonTriggers.length > 3 ? (
                            <li className="text-[11px] text-base-content/40">
                              +{options.activeDaemonTriggers.length - 3} more
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  {mode === 'create' && !state.isInitialLoading && state.selectableDaemonCount === 0 ? (
                    <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-warning">
                      {state.hasConnectedDaemon ? tLayout('noRunnerWithAgent') : tLayout('noRunnerConnected')}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg border border-base-300/70 bg-transparent px-3 py-2 text-xs font-medium text-base-content/70 transition hover:bg-base-200/30"
                    onClick={() => setIsAdvancedSettingsOpen((prev) => !prev)}
                    aria-expanded={isAdvancedSettingsOpen}
                  >
                    <span className="flex min-w-0 flex-col gap-0.5 text-left">
                      <span className="inline-flex items-center gap-1.5">
                        {tLayout('daemonAdvancedSettings')}
                        {state.isBoundDaemonOffline ? (
                          <span className="text-[10px] font-semibold text-warning">Offline</span>
                        ) : null}
                      </span>
                      {!isAdvancedSettingsOpen && advancedSettingsSummary ? (
                        <span className="truncate text-[11px] font-normal text-base-content/40">
                          {advancedSettingsSummary}
                        </span>
                      ) : null}
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 transition-transform',
                        isAdvancedSettingsOpen && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                  </button>
                  <div className={cn('space-y-2', isAdvancedSettingsOpen ? 'block' : 'hidden')}>
                    <div className="grid w-full grid-cols-2 gap-2">
                      <div className="flex flex-col min-w-0">
                        <label className="mb-1.5 text-xs font-medium text-base-content/50" htmlFor="daemon-select">
                          {tLayout('daemon')}
                          {state.isBoundDaemonOffline ? (
                            <span className="ml-1.5 text-[10px] font-semibold text-warning">Offline</span>
                          ) : null}
                        </label>
                        {shouldShowSelectSkeleton(options.daemonOptions.length) ? (
                          <SelectSkeleton />
                        ) : (
                          <Select
                            id="daemon-select"
                            size="sm"
                            value={state.daemonId}
                            onChange={actions.onDaemonIdChange}
                            options={options.daemonOptions}
                            buttonClassName={state.daemonConflictSelectButtonClassName}
                            fullWidth
                          />
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <label
                          className="mb-1.5 text-xs font-medium text-base-content/50"
                          htmlFor="daemon-agent-config-select"
                        >
                          Agent
                        </label>
                        {shouldShowSelectSkeleton(options.daemonAgentConfigOptions.length) ? (
                          <SelectSkeleton />
                        ) : (
                          <Select
                            id="daemon-agent-config-select"
                            size="sm"
                            value={state.daemonAgentConfigId}
                            onChange={actions.onDaemonAgentConfigIdChange}
                            options={options.daemonAgentConfigOptions}
                            buttonClassName={state.daemonConflictSelectButtonClassName}
                            fullWidth
                          />
                        )}
                      </div>
                    </div>
                    <div className="grid w-full grid-cols-2 gap-2">
                      <div className="flex flex-col min-w-0">
                        <label
                          className="mb-1.5 text-xs font-medium text-base-content/50"
                          htmlFor="daemon-runner-type-select"
                        >
                          {tLayout('runnerType')}
                        </label>
                        {shouldShowSelectSkeleton(options.daemonRunnerTypeOptions.length) ? (
                          <SelectSkeleton />
                        ) : (
                          <Select
                            id="daemon-runner-type-select"
                            size="sm"
                            value={state.daemonRunnerType}
                            onChange={actions.onDaemonRunnerTypeChange}
                            disabled={state.daemonAgentConfigId.length === 0}
                            options={options.daemonRunnerTypeOptions}
                            fullWidth
                          />
                        )}
                      </div>
                      <div className="relative flex flex-col min-w-0">
                        <label
                          className="mb-1.5 flex h-4 items-center text-xs font-medium text-base-content/50"
                          htmlFor="daemon-model-select"
                        >
                          {tLayout('runnerModel')}
                        </label>
                        {options.showEffortSelect ? (
                          <EffortSlider
                            className="absolute right-0 top-0 -translate-y-0.5"
                            id="daemon-effort-select"
                            value={state.daemonEffort}
                            onChange={actions.onDaemonEffortChange}
                            options={options.daemonEffortOptions}
                            title={tLayout('runnerEffort')}
                            autoLabel={tLayout('daemonEffortAuto')}
                            compactAutoLabel={tLayout('daemonEffortDefaultShort')}
                            fasterLabel={tLayout('daemonEffortFaster')}
                            smarterLabel={tLayout('daemonEffortSmarter')}
                          />
                        ) : null}
                        {shouldShowSelectSkeleton(options.daemonModelOptions.length) ? (
                          <SelectSkeleton />
                        ) : (
                          <Select
                            id="daemon-model-select"
                            size="sm"
                            value={state.daemonModel}
                            onChange={actions.onDaemonModelChange}
                            disabled={state.daemonRunnerType.length === 0}
                            options={options.daemonModelOptions}
                            fullWidth
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* TODO: stream-json 파싱 로그 적용 후 경고 제거 예정
                {state.daemonRunnerType === "CLAUDE_CODE" || state.daemonRunnerType === "AMP" ? (
                  <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs text-base-content/80">
                    {tLayout("runnerTypeClaudeCodeLogWarning")}
                  </div>
                ) : null}
                */}
              {mode === 'create' ? (
                <div>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <label
                        className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 transition ${
                          state.daemonPlanMode
                            ? 'border-primary/40 bg-primary/10 text-primary'
                            : 'border-base-300 text-base-content/70'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-xs"
                          checked={state.daemonPlanMode}
                          disabled={state.isPlanModeForced}
                          onChange={(event) => actions.onDaemonPlanModeChange(event.target.checked)}
                        />
                        <span className="text-xs font-medium">{tLayout('planModeLabel')}</span>
                      </label>
                      <label
                        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition ${state.worktree.isWorktreeUsageDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${
                          state.worktree.daemonUseWorktree
                            ? 'border-info/40 bg-info/10 text-info'
                            : 'border-base-300 text-base-content/70'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-xs"
                          checked={state.worktree.daemonUseWorktree}
                          disabled={state.worktree.isWorktreeUsageDisabled}
                          onChange={(event) => actions.worktree.onDaemonUseWorktreeChange(event.target.checked)}
                        />
                        <span className="text-xs font-medium">{tLayout('useWorktreeLabel')}</span>
                      </label>
                    </div>
                    {options.userConventionOptions.length > 0 ? (
                      <UserConventionSelector
                        label={tLayout('userConventionsLabel')}
                        emptyHint={tLayout('userConventionsEmpty')}
                        conventions={options.userConventionOptions}
                        selectedIds={state.selectedUserConventionIds}
                        onToggle={actions.onToggleUserConventionId}
                      />
                    ) : null}
                    {state.isSelectedDaemonPlanModeOnly ? (
                      <p className="text-xs text-primary">{tLayout('planModeOnlySharedRunnerNotice')}</p>
                    ) : null}
                    {state.worktree.daemonUseWorktree ? (
                      <DaemonTriggerWorktreeSettings
                        isOpen={isRunnerBoxSettingsOpen}
                        onOpenChange={setIsRunnerBoxSettingsOpen}
                        state={state.worktree}
                        options={options.worktree}
                        actions={actions.worktree}
                      />
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
            {mode === 'create' && state.dispatchPolicy.dispatchPolicies.length > 0 ? (
              <DaemonTriggerDispatchPolicyPicker state={state.dispatchPolicy} actions={actions.dispatchPolicy} />
            ) : null}
            {/* min-h-0 금지: 내부 드롭존의 최소 높이보다 작게 줄어들면 콘텐츠가 흘러넘쳐 푸터와 겹친다. 부족분은 스크롤 영역이 흡수한다. */}
            {/* onFocus는 자식(에디터)에서 버블링된다 → 프롬프트 진입 시 상단 설정을 1회 접는다. */}
            <div className="form-control w-full flex-1" onFocus={handlePromptFocus}>
              <span className="label-text mb-1.5 text-xs font-medium text-base-content/50">
                {selectedDispatchPolicy ? 'Append Prompt' : 'Prompt'}
              </span>
              {/* File drops on the prompt are attachment-first; revisit this wrapper before adding editor-level file drop handling. */}
              <AttachmentDropZone
                drafts={state.attachmentDrafts}
                onChange={actions.onAttachmentDraftsChange}
                disabled={state.isSubmitPending}
                className={cn(
                  'tiptap-compact-wrapper flex-1 overflow-y-auto rounded-lg border transition-colors sm:max-h-80 [&_.ProseMirror]:!px-3 [&_.ProseMirror]:!py-2',
                  // 정책 선택 시 프롬프트는 프리셋이 주도하는 보조 입력이라 바닥 높이를 낮춰 세로 공간을 아낀다.
                  selectedDispatchPolicy ? 'min-h-28' : 'min-h-40',
                  state.isPromptOverLimit ? 'border-error/60' : 'border-base-300',
                )}
                activeClassName="border-primary/60 bg-primary/5"
              >
                <TiptapEditor
                  value={state.daemonPrompt}
                  onChangeAction={actions.onDaemonPromptChange}
                  placeholder={tLayout(
                    selectedDispatchPolicy ? 'daemonPromptPlaceholderWithPolicy' : 'daemonPromptPlaceholder',
                  )}
                  hideToolbar
                  compact
                />
              </AttachmentDropZone>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-base-content/40">{tLayout('daemonPromptSlashHint')}</span>
                <span className={`text-xs ${state.isPromptOverLimit ? 'text-error' : 'text-base-content/40'}`}>
                  {state.promptTextLength} / {state.promptMaxLength}
                </span>
              </div>
            </div>
          </div>
          {/* 고정 푸터: 본문 스크롤과 무관하게 첨부 칩·충돌 배너·액션 버튼을 항상 노출한다. border-t로 스크롤 영역과의 경계를 드러낸다. */}
          <div className="shrink-0 space-y-3 border-t border-base-300 pt-3">
            <AttachmentPendingChips
              drafts={state.attachmentDrafts}
              onChange={actions.onAttachmentDraftsChange}
              disabled={state.isSubmitPending}
            />
            {shouldShowResolvedSettingsNotice ? (
              <div className="space-y-1 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-base-content/75">
                <p className="font-semibold text-warning">{tLayout('resolvedSettingsNoticeTitle')}</p>
                {state.isResolvedSettingsError ? (
                  <p className="text-warning">{tLayout('resolvedSettingsFailed')}</p>
                ) : null}
                {resolvedSettingsErrors.length > 0 ? (
                  <ul className="space-y-1">
                    {resolvedSettingsErrors.map((error) => (
                      <li key={error.detail} className="text-error">
                        {tApiErrorDetails(error.detail as 'RUNNER_MODEL_INVALID')}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {state.activeConcurrencyWarningReason ? (
              <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-base-content/80">
                {tLayout(activeConcurrencyWarningMessageKey(state.activeConcurrencyWarningReason))}
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                {preset?.token && preset.projectId ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm gap-1.5"
                    onClick={() => setIsPresetModalOpen(true)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                      />
                    </svg>
                    {tLayout('promptPresetButton')}
                  </button>
                ) : null}
                <AttachmentAddButton
                  drafts={state.attachmentDrafts}
                  onChange={actions.onAttachmentDraftsChange}
                  disabled={state.isSubmitPending}
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={actions.onClose}
                  disabled={state.isSubmitPending}
                >
                  {tCommon('actions.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={actions.onSubmit}
                  disabled={state.isSubmitDisabled}
                >
                  {mode === 'continue'
                    ? state.isSubmitPending
                      ? tLayout('requesting')
                      : tLayout('sendContinue')
                    : state.isSubmitPending
                      ? tLayout('requesting')
                      : tLayout('sendRequest')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </BaseModal>
      {preset?.token && preset.projectId ? (
        <PromptPresetModal
          isOpen={isPresetModalOpen}
          onClose={() => setIsPresetModalOpen(false)}
          token={preset.token}
          projectId={preset.projectId}
          memberId={preset.memberId}
          canWrite={preset.canWrite}
          onSelectPreset={(prompt, userConventionIds) => {
            actions.onDaemonPromptChange(appendPromptContent(state.daemonPrompt, prompt));
            actions.onUserConventionIdsChange(userConventionIds);
          }}
          currentPrompt={state.daemonPrompt}
          currentUserConventionIds={state.selectedUserConventionIds}
          userConventionOptions={options.userConventionOptions}
        />
      ) : null}
      <BaseModal
        isOpen={Boolean(state.worktree.worktreeWarningMessage)}
        onClose={actions.worktree.onDismissWorktreeWarning}
        variant="bottomSheet"
        size="md"
        backdropClassName="[backface-visibility:hidden]"
      >
        <h3 className="text-base font-semibold">{tLayout('daemonWorktreeWarningTitle')}</h3>
        <p className="mt-2 text-sm text-base-content/70">{state.worktree.worktreeWarningMessage}</p>
        <div className="mt-4 flex justify-end">
          <button type="button" className="btn btn-primary btn-sm" onClick={actions.worktree.onDismissWorktreeWarning}>
            {tCommon('actions.confirm')}
          </button>
        </div>
      </BaseModal>
    </>
  );
};
